import moment from 'moment';

import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import {
  EAdserverCreativeType,
  EBannerRank,
  IAdserverCreative,
  IAdserverCreativeFlightDatesSubgroup,
  TargetBoolTypeAll,
  TargetBoolTypeFalse,
  TargetBoolTypeTrue,
  TargetingCoppa0,
  TargetingCoppa1,
  TargetingCoppaAll,
  TargetingGDPR0,
  TargetingGDPR1,
  TargetingGDPRAll,
} from '~/src/dash/api/console-api';
import {
  FrequencyCapActiveActive,
  FrequencyTargetingKey,
  FrequencyTrackingKey,
} from '~/src/dash/api/frequency-cap';
import { IObject } from '~/src/dash/api/resource-transforms';
import { IIncludeExcludeTableElement } from '~/src/dash/components/include-exclude-table';
import {
  ILocalTargetingParameter,
  validateLocalTargeting,
  validateLocalTargetingRule,
} from '~/src/dash/components/local-targeting/local-targeting';
import {
  CreativeTypeMediafile,
  CreativeTypeVast,
  WeightingTypeImps,
  WeightingTypeRequests,
} from '~/src/dash/globals/constants';
import { validateDatetimes } from '~/src/dash/utils/date';
import { IDeviceType } from '~/src/dash/utils/devices';
import { isPhiloPublisher } from '~/src/dash/utils/ui';
import { validatorFactory } from '~/src/dash/utils/validator';
import { PID } from '~/src/dash/utils/publishers';

export enum SelectMode {
  Allowlist = 0,
  Denylist,
}

// the coppa options for the dropdown
export const coppaOptions = [
  { key: TargetingCoppaAll, text: 'All' },
  { key: TargetingCoppa0, text: '0' },
  { key: TargetingCoppa1, text: '1' },
];

// the LAT options for the dropdown
export const latOptions = [
  { key: TargetBoolTypeAll, text: 'All' },
  { key: TargetBoolTypeFalse, text: 'False' },
  { key: TargetBoolTypeTrue, text: 'True' },
];

// the Livestream options for the dropdown
export const livestreamOptions = [
  { key: 0, text: 'All' },
  { key: 1, text: 'Live' },
  { key: 2, text: 'VOD' },
  { key: 3, text: 'DVR' },
];

export const GDPROptions = [
  { key: TargetingGDPRAll, text: 'All' },
  { key: TargetingGDPR0, text: '0' },
  { key: TargetingGDPR1, text: '1' },
];

export const GDPRConsentOptions = [
  { key: TargetBoolTypeAll, text: 'All' },
  { key: TargetBoolTypeFalse, text: 'False' },
  { key: TargetBoolTypeTrue, text: 'True' },
];

export const FLIGHT_DATES = 'Flight dates';
export const REACTIVATE_BIDDERS = 'Reactivate Bidder';

// regex that tests if an IAB string is correct or not
// see https://regex101.com/r/Hivbch/1
export const iabRegexp = /^IAB\d+(-\d+)?$/;

// this turns IAB1-2 into 1.2, IAB1 into 1.0, IAB17-2 into 17.2, IAB17 into 17.01 so they are sorted in numeric order
export function sortIABCats(
  iabCatA: IIncludeExcludeTableElement,
  iabCatB: IIncludeExcludeTableElement,
) {
  const onlyNumRegexp = /[^\d.-]/g;
  const catAIDString = iabCatA.ID.toString() || '';
  const catBIDString = iabCatB.ID.toString() || '';
  let catA = 0;
  let catB = 0;
  // replace letters with "", replace - with '.' -> parseFloat
  if (!catAIDString.includes('-')) {
    catA = parseFloat((catAIDString + '.01').replace(onlyNumRegexp, ''));
  } else {
    catA = parseFloat(catAIDString.replace('-', '.').replace(onlyNumRegexp, ''));
  }
  if (!catBIDString.includes('-')) {
    catB = parseFloat((catBIDString + '.01').replace(onlyNumRegexp, ''));
  } else {
    catB = parseFloat(catBIDString.replace('-', '.').replace(onlyNumRegexp, ''));
  }
  if (catA < catB) {
    return -1;
  }
  return 0;
}

export const flagBanners = (creatives: IAdserverCreative[]) => {
  let assignedPrimaryBanner: boolean = false;
  const flagged: IAdserverCreative[] = [];
  for (const c of creatives) {
    if (c?.CreativeType === EAdserverCreativeType.Banner) {
      c.Weight = '0';
      if (!assignedPrimaryBanner) {
        c.BannerRank = EBannerRank.Primary;
        assignedPrimaryBanner = true;
      } else {
        c.BannerRank = EBannerRank.Secondary;
      }
    }
    flagged.push(c);
  }
  return flagged;
};

// Converts Adserver Creatives object to IAdserverCreativeFlightDatesSubgroup object
// separates the creatives by Flight Date
export const adserverCreativesToSubgroups = (creatives: IAdserverCreative[]) => {
  const adserverCreativeFlightDatesSubgroups: IAdserverCreativeFlightDatesSubgroup[] = [];
  let StartDate = 'null';
  let EndDate = 'null';

  creatives.forEach((creative: IAdserverCreative) => {
    let selectedSubgroupIndex = -1;
    if (adserverCreativeFlightDatesSubgroups.length === 0) {
      StartDate = creative.StartDate;
      EndDate = creative.EndDate;
      adserverCreativeFlightDatesSubgroups.push(getEmptyCreativeCheckpoint(StartDate, EndDate));
      adserverCreativeFlightDatesSubgroups[0].AdserverCreatives.push(creative);
    } else {
      adserverCreativeFlightDatesSubgroups.forEach(
        (subgroup: IAdserverCreativeFlightDatesSubgroup, sgIndex: number) => {
          if (subgroup.StartDate === creative.StartDate && subgroup.EndDate === creative.EndDate) {
            selectedSubgroupIndex = sgIndex;
          }
        },
      );
      if (selectedSubgroupIndex < 0) {
        StartDate = creative.StartDate;
        EndDate = creative.EndDate;
        adserverCreativeFlightDatesSubgroups.push(getEmptyCreativeCheckpoint(StartDate, EndDate));
        adserverCreativeFlightDatesSubgroups[
          adserverCreativeFlightDatesSubgroups.length - 1
        ].AdserverCreatives.push(creative);
      } else {
        adserverCreativeFlightDatesSubgroups[selectedSubgroupIndex].AdserverCreatives.push(
          creative,
        );
      }
    }
  });

  if (adserverCreativeFlightDatesSubgroups !== undefined) {
    for (let i = 0; i < adserverCreativeFlightDatesSubgroups.length; i++) {
      let totalWeight = 0;
      for (let j = 0; j < adserverCreativeFlightDatesSubgroups[i].AdserverCreatives.length; j++) {
        totalWeight += Number(adserverCreativeFlightDatesSubgroups[i].AdserverCreatives[j].Weight);
      }
      adserverCreativeFlightDatesSubgroups[i].TotalWeight = totalWeight;
    }
  }

  return adserverCreativeFlightDatesSubgroups;
};

export function isExistingCampaign(campaign: IAdserverCampaign): boolean {
  return !!campaign.ID;
}

export function getPercentageNumber(weight: number, weights: number): number {
  if (weights === 0) {
    return 0;
  }
  return (weight * 100) / weights;
}

export const CreativeTypes = [
  {
    label: CreativeTypeVast,
    value: CreativeTypeVast,
  },
  {
    label: CreativeTypeMediafile,
    value: CreativeTypeMediafile,
  },
];

export const WeightingTypes = [
  {
    label: WeightingTypeImps,
    value: WeightingTypeImps,
  },
  {
    label: WeightingTypeRequests,
    value: WeightingTypeRequests,
  },
];

export const getEmptyCreativeCheckpoint = (StartDate: string, EndDate: string) => {
  return {
    ID: '0',
    AdserverCreatives: [] as IAdserverCreative[],
    TotalWeight: 0,
    StartDate,
    EndDate,
    errors: '',
  };
};

export const isBannerType = (creative: IAdserverCreative): boolean => {
  return creative?.CreativeType === EAdserverCreativeType.Banner;
};

export const ResolutionTypes: IDeviceType[] = [
  {
    key: '1200p',
    text: '1200p',
  },
  {
    key: '1080p',
    text: '1080p',
  },
  {
    key: '720p',
    text: '720p',
  },
  {
    key: '480p',
    text: '480p',
  },
  {
    key: '360p',
    text: '360p',
  },
];

export const TrackerEventType = [
  {
    label: 'Impression',
    value: 'impression',
  },
  {
    label: 'Start',
    value: 'start',
  },
  {
    label: 'First Quartile',
    value: 'firstQuartile',
  },
  {
    label: 'Midpoint',
    value: 'midpoint',
  },
  {
    label: 'Third Quartile',
    value: 'thirdQuartile',
  },
  {
    label: 'Complete',
    value: 'complete',
  },
  {
    label: 'Mute',
    value: 'mute',
  },
  {
    label: 'Unmute',
    value: 'unmute',
  },
  {
    label: 'Rewind',
    value: 'rewind',
  },
  {
    label: 'Pause',
    value: 'pause',
  },
  {
    label: 'Resume',
    value: 'resume',
  },
  {
    label: 'Fullscreen',
    value: 'fullscreen',
  },
  {
    label: 'Click',
    value: 'click',
  },
];

export const FrequencyTrackingKeys = [
  {
    label: 'Device ID',
    value: FrequencyTrackingKey.DEVICEID,
  },
  {
    label: 'IP',
    value: FrequencyTrackingKey.IP,
  },
];

export const getFrequencyTargetingKeysForPublisher = (publisherID: string) => {
  if (isPhiloPublisher(publisherID) || publisherID === PID.waipu) {
    return [
      {
        label: 'Impression',
        value: FrequencyTargetingKey.IMPRESSION,
      },
      {
        label: 'Duration',
        value: FrequencyTargetingKey.DURATION,
      },
      {
        label: 'Bid Won',
        value: FrequencyTargetingKey.BID_WON,
      },
    ];
  } else {
    return [
      {
        label: 'Impression',
        value: FrequencyTargetingKey.IMPRESSION,
      },
      {
        label: 'Duration',
        value: FrequencyTargetingKey.DURATION,
      },
    ];
  }
};

export const getFrequencyTargetingKeysWithoutDurationForPublisher = (publisherID: string) => {
  if (isPhiloPublisher(publisherID) || publisherID === PID.waipu) {
    return [
      {
        label: 'Impression',
        value: FrequencyTargetingKey.IMPRESSION,
      },
      {
        label: 'Bid Won',
        value: FrequencyTargetingKey.BID_WON,
      },
    ];
  } else {
    return [
      {
        label: 'Impression',
        value: FrequencyTargetingKey.IMPRESSION,
      },
    ];
  }
};

export const validate = validatorFactory({
  AdserverCampaignTrackers(_: any, _key: string, _obj: IObject): string {
    for (const tracker of _obj.AdserverCampaignTrackers) {
      const trimmedURL = tracker.URL.trim();
      const trimmedType = tracker.Type.trim();

      // if a tracker has either a URL or Type, but not both, then it is not a valid tracker
      // note: empty trackers will be removed before saving the campaign
      if ((trimmedURL !== '' && trimmedType === '') || (trimmedURL === '' && trimmedType !== '')) {
        return 'Please ensure that all trackers have a non-empty URL and type';
      }
    }
    return '';
  },
  FrequencyLimit(val: any, _key: string, _obj: IObject): string {
    if (_obj.FrequencyActive === FrequencyCapActiveActive) {
      if (!val || isNaN(parseInt(val, 10))) {
        return 'Please enter a valid number';
      }
      if (!val || parseInt(val, 10) === 0) {
        return 'Please enter an integer larger than 0';
      }
    }
    return '';
  },
  FrequencyLimitDays(val: any, _key: string, _obj: IObject): string {
    if (_obj.FrequencyActive === FrequencyCapActiveActive) {
      if (!val || isNaN(parseInt(val, 10))) {
        return 'Please enter a valid number';
      }
      if (!val || parseInt(val, 10) < 0) {
        return 'Please enter a positive integer';
      }
      if (!val || parseInt(val, 10) > 30) {
        return 'Please enter an integer smaller than 30';
      }
    }
    return '';
  },
  FrequencyLimitHours(val: any, _key: string, _obj: IObject): string {
    if (_obj.FrequencyActive === FrequencyCapActiveActive) {
      if (!val || isNaN(parseInt(val, 10))) {
        return 'Please enter a valid number';
      }
      if (!val || parseInt(val, 10) < 0) {
        return 'Please enter a positive integer';
      }
    }
    return '';
  },
  DateTimes(_: any, _key: string, _obj: IObject): string {
    const minDate = _obj.ID === '' ? moment().utc(true).startOf('day') : undefined;
    const errMsgs = validateDatetimes(_obj.dateStart, _obj.dateEnd, minDate);
    if (errMsgs.length > 0) {
      return errMsgs.join('. ');
    }
    return '';
  },
  Quantity(val: any): string {
    if (!val || isNaN(parseInt(val, 10))) {
      return 'Please enter a valid number';
    }
    if (!val || parseInt(val, 10) === 0) {
      return 'Please enter an integer larger than 0';
    }
    return '';
  },
  GrossCPM(val: any, _key: string, _obj: IObject): string {
    // if GrossCPM is active, the gross cpm must be more than net cpm
    if (_obj.isGrossCPMActive && val && parseInt(val, 10) < parseInt(_obj.CPM, 10)) {
      return 'Your Gross CPM cannot be less than your Net CPM';
    } else if (!_obj.isGrossCPMActive && val && parseInt(val, 10) !== 0) {
      // if GrossCPM is not active, then the value must be 0, the field will be hidden once the switch is flipped but this is another validation
      return 'Your Gross CPM must be 0 if Gross CPM is not active';
    }
    return '';
  },
  SiteWeightsActive(): string {
    return '';
  },
  localTargetingAllowedParameters(
    val: ILocalTargetingParameter[],
    _key: string,
    _obj: IObject,
  ): string {
    return validateLocalTargetingRule(val, _obj.localTargetingDisallowedParameters).join('; ');
  },
  localTargetingDisallowedParameters(val: ILocalTargetingParameter[]): string {
    return validateLocalTargeting(val).join('; ');
  },
  AdserverCreatives(val: any): string {
    // confirm the weights add up to 100 and each weight is a number
    let totalWeight = 0;
    // val is adserverCreatives, if there are no creatives, we don't check the weight
    if (val.length === 0) {
      return '';
    }
    let numberOfNonBannerCreatives = 0;
    if (val) {
      for (const creative of val) {
        if (!isBannerType(creative)) {
          numberOfNonBannerCreatives++;
          const weightNum = Number(creative.Weight);
          if (Number.isNaN(weightNum) || creative.Weight === '') {
            return 'All non-banner creatives must have a weight assigned (1-100) and add up to 100.';
          }
          totalWeight += weightNum;
        }
      }
    }
    if (totalWeight % 100 !== 0 && numberOfNonBannerCreatives !== 0) {
      return `The total weight (${totalWeight}) for creatives must equal 100 overall or 100 per checkpoint if Creative Checkpoints are active`;
    }
    return '';
  },
  AdserverCreativeFlightDatesSubgroups(val: any): string {
    // val is adserverCreativeFlightDatesSubgroups, if there are no creatives, we don't check anything
    if (val !== undefined) {
      if (val.length === 0) {
        return '';
      }
    }

    const adserverCreativeFlightDatesSubgroups = val as IAdserverCreativeFlightDatesSubgroup[];

    if (val) {
      // We convert string value to Date for a better comparation, both having the same timezone
      let endDate = 'null';
      let endDateUTC = new Date(adserverCreativeFlightDatesSubgroups[0].EndDate);
      for (const checkpoint of adserverCreativeFlightDatesSubgroups) {
        if (endDate === 'null') {
          endDateUTC = new Date(checkpoint.EndDate);
          endDate = checkpoint.EndDate;
        } else {
          const startDateUTC = new Date(checkpoint.StartDate);
          if (endDateUTC >= startDateUTC) {
            return 'Dates for checkpoints cannot overlap';
          } else {
            endDate = checkpoint.EndDate;
            endDateUTC = new Date(endDate);
          }
        }
      }
    }
    return '';
  },
  Name(val: string): string {
    if (!val) {
      return 'Name cannot be empty';
    }
    return '';
  },
});
